export const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
export const BANK_ACCOUNT_NUMBER_REGEX = /^\d{9,18}$/;
export const INDIAN_MOBILE_REGEX = /^[6-9]\d{9}$/;
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const OTP_REGEX = /^\d{6}$/;

export const KNOWN_PAYOUT_BANK_OPTIONS = [
  "Select bank",
  "HDFC Bank",
  "ICICI Bank",
  "State Bank of India",
  "Axis Bank",
  "Kotak Bank Ltd",
  "Punjab National Bank",
  "Bharat Bank",
];

export const BANK_IFSC_PREFIXES: Record<string, string[]> = {
  "HDFC Bank": ["HDFC"],
  "ICICI Bank": ["ICIC"],
  "State Bank of India": ["SBIN"],
  "Axis Bank": ["UTIB"],
  "Kotak Bank Ltd": ["KKBK"],
  "Punjab National Bank": ["PUNB"],
  "Bharat Bank": ["BCBM", "BHAR"],
};

export const digitsOnly = (value: unknown) => String(value ?? "").replace(/\D/g, "");

export const normalizeBankAccountNumber = (value: unknown) =>
  digitsOnly(value).slice(0, 18);

export const normalizeIfsc = (value: unknown) =>
  String(value ?? "")
    .replace(/\s/g, "")
    .toUpperCase()
    .slice(0, 11);

export const normalizeEmail = (value: unknown) => String(value ?? "").trim();

export const normalizeIndianMobile = (value: unknown) =>
  digitsOnly(value).slice(-10);

export const isValidIfsc = (value: unknown) => IFSC_REGEX.test(normalizeIfsc(value));

export const isValidBankAccountNumber = (value: unknown) =>
  BANK_ACCOUNT_NUMBER_REGEX.test(normalizeBankAccountNumber(value));

export const isValidIndianMobile = (value: unknown) =>
  INDIAN_MOBILE_REGEX.test(normalizeIndianMobile(value));

export const isValidEmail = (value: unknown) => EMAIL_REGEX.test(normalizeEmail(value));

export const isValidOtpCode = (value: unknown) => OTP_REGEX.test(digitsOnly(value));

export const isSelectedValue = (value: string) => value.trim() && !value.startsWith("Select");

export const isIfscBankMismatch = (bankName: string, ifsc: string) => {
  const normalizedIfsc = normalizeIfsc(ifsc);
  const allowedPrefixes = BANK_IFSC_PREFIXES[bankName] || [];
  if (!isSelectedValue(bankName) || !allowedPrefixes.length || !isValidIfsc(normalizedIfsc)) {
    return false;
  }
  return !allowedPrefixes.includes(normalizedIfsc.slice(0, 4));
};

export const validateRequiredText = (
  value: unknown,
  emptyMessage: string,
  minLength?: { value: number; message: string },
) => {
  const normalized = String(value ?? "").trim();
  if (!normalized) return emptyMessage;
  if (minLength && normalized.length < minLength.value) return minLength.message;
  return "";
};

export const validateRequiredBankAccountNumber = (
  value: unknown,
  emptyMessage = "Enter bank account number.",
) => {
  const normalized = normalizeBankAccountNumber(value);
  if (!normalized) return emptyMessage;
  if (!BANK_ACCOUNT_NUMBER_REGEX.test(normalized)) {
    return "Account number must be 9 to 18 digits.";
  }
  return "";
};

export const validateRequiredConfirmAccountNumber = (
  value: unknown,
  accountNumber: unknown,
  emptyMessage = "Confirm bank account number.",
) => {
  const normalized = normalizeBankAccountNumber(value);
  if (!normalized) return emptyMessage;
  if (normalized !== normalizeBankAccountNumber(accountNumber)) {
    return "Account numbers do not match.";
  }
  return "";
};

export const validateRequiredIfsc = (
  value: unknown,
  bankName?: string,
  emptyMessage = "Enter IFSC code.",
) => {
  const normalized = normalizeIfsc(value);
  if (!normalized) return emptyMessage;
  if (!IFSC_REGEX.test(normalized)) return "Enter a valid IFSC code, e.g. HDFC0001234.";
  if (bankName && isIfscBankMismatch(bankName, normalized)) {
    return "IFSC bank code does not match selected bank.";
  }
  return "";
};

export const validateRequiredIndianMobile = (
  value: unknown,
  emptyMessage = "Enter mobile number.",
) => {
  const normalized = normalizeIndianMobile(value);
  if (!normalized) return emptyMessage;
  if (!INDIAN_MOBILE_REGEX.test(normalized)) {
    return "Enter a valid 10-digit Indian mobile number.";
  }
  return "";
};

export const validateOptionalIndianMobile = (
  value: unknown,
  invalidMessage = "Enter a valid 10-digit mobile number.",
) => {
  const normalized = normalizeIndianMobile(value);
  if (!normalized) return "";
  return INDIAN_MOBILE_REGEX.test(normalized) ? "" : invalidMessage;
};

export const validateRequiredEmail = (
  value: unknown,
  emptyMessage = "Enter email address.",
) => {
  const normalized = normalizeEmail(value);
  if (!normalized) return emptyMessage;
  if (!EMAIL_REGEX.test(normalized)) return "Enter a valid email address.";
  return "";
};

export const validateOptionalEmail = (
  value: unknown,
  invalidMessage = "Enter a valid email address.",
) => {
  const normalized = normalizeEmail(value);
  if (!normalized) return "";
  return EMAIL_REGEX.test(normalized) ? "" : invalidMessage;
};

export const validateOtpCode = (
  value: unknown,
  message = "Enter the 6-digit verification code.",
) => (isValidOtpCode(value) ? "" : message);

export const normalizeAmountInput = (value: unknown) =>
  String(value ?? "").replace(/,/g, "").trim();

export const validatePositiveAmountInput = (
  value: unknown,
  message = "Please add a valid amount condition before saving.",
) => {
  const normalized = normalizeAmountInput(value);
  if (!normalized || Number(normalized) <= 0 || Number.isNaN(Number(normalized))) {
    return message;
  }
  return "";
};

export const validateTransferModeForAmount = (amountPaise: number, mode: string) => {
  const normalizedMode = mode.trim().toUpperCase();
  if (!["NEFT", "IMPS", "RTGS"].includes(normalizedMode)) {
    return "method must be NEFT, IMPS, or RTGS";
  }
  if (amountPaise > 20000000 && normalizedMode !== "RTGS") {
    return "transfers above Rs 2,00,000 must use RTGS";
  }
  if (amountPaise <= 20000000 && normalizedMode === "RTGS") {
    return "transfers up to Rs 2,00,000 must use NEFT or IMPS";
  }
  return "";
};
